import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { ArrowLeft, Trash2 } from "lucide-react";

import { useViewSingleEnquiry } from "../../hooks/enquiries/viewSingleEnquiry";
import { useUpdateEnquiryStatus } from "../../hooks/enquiries/updateEnquiryStatus";
import { useDeleteEnquiry } from "../../hooks/enquiries/deleteEnquiry";
import { useEnquiryContext } from "../../context/EnquiryContext";
import DeleteModal from "../../components/modal/deleteModal";
import Button from "../../components/common/button";

/* ─────────────────────────────────────────
   STATUS BADGE
───────────────────────────────────────── */
const STATUS_STYLES = {
  pending:  { bg: "#fef9c3", color: "#854d0e", border: "#fde047" },
  closed:   { bg: "#fee2e2", color: "#991b1b", border: "#fca5a5" },
};

const StatusBadge = ({ status }) => {
  const key = String(status || "pending").toLowerCase();
  const style = STATUS_STYLES[key] || { bg: "#f3f4f6", color: "#374151", border: "#d1d5db" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "4px 14px",
        borderRadius: "999px",
        fontSize: "13px",
        fontWeight: 600,
        background: style.bg,
        color: style.color,
        border: `1px solid ${style.border}`,
        textTransform: "capitalize",
      }}
    >
      {key}
    </span>
  );
};

/* ─────────────────────────────────────────
   STATUS CHANGE BUTTON
───────────────────────────────────────── */
const STATUS_ACTIONS = [
  { label: "Mark as Pending",  value: "pending",  bg: "#5c308d", hover: "#4c2a75" },
  { label: "Close Enquiry",    value: "closed",   bg: "#dc2626", hover: "#b91c1c" },
];

/* ─────────────────────────────────────────
   HELPERS
───────────────────────────────────────── */
const resolveUserName = (user) => {
  if (!user) return null;
  if (typeof user === "string") return user;
  return user.name || user.email || null;
};

const resolveClientName = (client) => {
  if (!client) return null;
  if (typeof client === "string") return client;
  return client.name || null;
};

const resolveProducts = (products) => {
  if (!products) return null;
  const arr = Array.isArray(products) ? products : [products];
  if (arr.length === 0) return null;
  return arr.map((p) => (typeof p === "string" ? p : p?.title || p?.name || p?._id)).join(", ");
};

const resolveLocation = (location) => {
  if (!location) return null;
  if (typeof location === "string") return location;
  return location.city
    ? [location.city, location.country].filter(Boolean).join(", ")
    : location.country || location.name || null;
};

/* ─────────────────────────────────────────
   FIELD ROW
───────────────────────────────────────── */
const Field = ({ label, value, fullWidth = false }) => (
  <div
    className="field"
    style={fullWidth ? { gridColumn: "1 / -1" } : {}}
  >
    <span className="label">{label}</span>
    <span className="value" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
      {value || "-"}
    </span>
  </div>
);

/* ─────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────── */
const ViewEnquiry = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { markAsRead } = useEnquiryContext();

  const { data, isLoading, isError } = useViewSingleEnquiry(id);
  const { mutateAsync: updateStatus, isPending: isUpdating } = useUpdateEnquiryStatus();
  const { mutateAsync: deleteEnquiry, isPending: isDeleting } = useDeleteEnquiry();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [activeAction, setActiveAction] = useState(null); // which status button is loading

  // Mark enquiry as read when it's opened
  useEffect(() => {
    markAsRead(id);
  }, [id, markAsRead]);

  // Normalise response shape — handle common envelope patterns
  const enquiry =
    data?.data?.enquiry ||
    data?.data?.data ||
    data?.enquiry ||
    data?.data ||
    data ||
    {};

  /* ── HANDLERS ── */
  const handleStatusChange = async (newStatus) => {
    setActiveAction(newStatus);
    try {
      await updateStatus({ id, status: newStatus });
      toast.success(`Enquiry marked as ${newStatus}`);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update status");
    } finally {
      setActiveAction(null);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteEnquiry(id);
      toast.success("Enquiry deleted successfully");
      navigate("/enquiries");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete enquiry");
    }
  };

  const formatDate = (val) =>
    val
      ? new Date(val).toLocaleString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "-";

  /* ── STATES ── */
  if (isLoading) return <div>Loading enquiry...</div>;
  if (isError)   return <div>Error loading enquiry</div>;

  /* ── RENDER ── */
  return (
    <>
      {/* BACK BUTTON */}
      <h5>
        <button className="back-btn" onClick={() => navigate(-1)}>
          <ArrowLeft size={18} /> Back to Enquiries
        </button>
      </h5>

      <div className="page-body" style={{ marginTop: 30 }}>
        <div
          className="client-view-card"
          style={{
            padding: "24px",
            border: "1px solid #ddd",
            borderRadius: "10px",
            background: "#fff",
            width: "100%",
          }}
        >
          {/* CARD HEADER */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: "12px",
              marginBottom: "24px",
              paddingBottom: "16px",
              borderBottom: "1px solid #f0f0f0",
            }}
          >
            <div>
              <h2 style={{ margin: 0, fontSize: "18px", color: "#1f2937" }}>
                {resolveUserName(enquiry?.user) || enquiry?.name || "Enquiry Details"}
              </h2>
              <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#6b7280" }}>
                {enquiry?.email && <span style={{ marginRight: 12 }}>{enquiry.email}</span>}
                Received: {formatDate(enquiry?.createdAt)}
              </p>
            </div>
            <StatusBadge status={enquiry?.status} />
          </div>

          {/* FIELDS GRID */}
          <div className="client-grid">
            {/* Sender info */}
            <Field label="User Name"    value={resolveUserName(enquiry?.user) || enquiry?.name} />
            <Field label="Client Name"  value={resolveClientName(enquiry?.client) || enquiry?.company} />
            <Field label="Products"     value={resolveProducts(enquiry?.products) || resolveProducts(enquiry?.product)} />
            <Field label="Location"     value={resolveLocation(enquiry?.location) || enquiry?.country || enquiry?.city} />
            <Field label="Email"        value={enquiry?.email} />
            <Field label="Phone"        value={enquiry?.phone} />
            <Field label="Status"       value={enquiry?.status} />
            <Field label="Received"     value={formatDate(enquiry?.createdAt)} />
            <Field label="Last Updated" value={formatDate(enquiry?.updatedAt)} />
            <Field
              label="Description"
              value={enquiry?.description || enquiry?.message}
              fullWidth
            />
          </div>

          {/* STATUS ACTIONS */}
          <div
            style={{
              marginTop: "28px",
              paddingTop: "20px",
              borderTop: "1px solid #f0f0f0",
            }}
          >
            <p
              style={{
                margin: "0 0 12px",
                fontSize: "13px",
                fontWeight: 600,
                color: "#374151",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Update Status
            </p>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
              {STATUS_ACTIONS.map(({ label, value, bg }) => (
                <button
                  key={value}
                  onClick={() => handleStatusChange(value)}
                  disabled={
                    isUpdating ||
                    String(enquiry?.status || "").toLowerCase() === value
                  }
                  style={{
                    padding: "8px 16px",
                    borderRadius: "6px",
                    border: "none",
                    background: bg,
                    color: "#fff",
                    fontSize: "13px",
                    fontWeight: 600,
                    cursor:
                      isUpdating ||
                      String(enquiry?.status || "").toLowerCase() === value
                        ? "not-allowed"
                        : "pointer",
                    opacity:
                      isUpdating ||
                      String(enquiry?.status || "").toLowerCase() === value
                        ? 0.55
                        : 1,
                    transition: "opacity 0.2s",
                  }}
                >
                  {activeAction === value ? "Updating..." : label}
                </button>
              ))}

              {/* DELETE BUTTON – separated to the right */}
              <button
                onClick={() => setIsDeleteModalOpen(true)}
                disabled={isDeleting}
                style={{
                  marginLeft: "auto",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  border: "1px solid #fca5a5",
                  background: "#fff",
                  color: "#dc2626",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: isDeleting ? "not-allowed" : "pointer",
                  opacity: isDeleting ? 0.55 : 1,
                }}
              >
                <Trash2 size={14} />
                {isDeleting ? "Deleting..." : "Delete Enquiry"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* DELETE CONFIRM MODAL */}
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Enquiry"
        itemName={enquiry?.subject || enquiry?.name}
        isLoading={isDeleting}
      />
    </>
  );
};

export default ViewEnquiry;
